import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Zap, Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { LanguageSwitch } from '../../i18n/LanguageSwitch'
import { useLocale } from '../../i18n/LocaleProvider'
import { signIn, resendConfirmationCode, getCognitoErrorMessage } from '../../lib/cognito'
import { setTokens } from '../../lib/auth'
import { bootstrapAuthenticatedUser, logoutAfterBootstrapFailure } from '../../lib/authBootstrap'
import { buildConfirmEmailPath, isUserNotConfirmedError, normalizeEmail } from '../../lib/emailVerification'
import { WorkspaceBootstrapErrorView } from './WorkspaceBootstrapError'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'

export function SignIn({ verified = false, initialEmail = '', redirectTo = '/dashboard' }) {
  const navigate = useNavigate()
  const { t } = useLocale()
  const { loadUser } = useAuthStore()
  const { fetchOrg, fetchUsage } = useOrgStore()

  const [email, setEmail] = useState(normalizeEmail(initialEmail))
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState('')
  const [unconfirmedEmail, setUnconfirmedEmail] = useState('')
  const [notice, setNotice] = useState(verified ? 'auth.signIn.verifiedNotice' : '')
  const [bootstrapError, setBootstrapError] = useState(false)

  async function runBootstrap(normalizedEmail) {
    const result = await bootstrapAuthenticatedUser({
      email: normalizedEmail,
      destination: redirectTo || '/dashboard',
    })

    await loadUser()
    await fetchOrg()
    await fetchUsage()

    if (result.warning) {
      alert(result.warning)
    }
    navigate(result.destination || redirectTo || '/dashboard')
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const normalizedEmail = normalizeEmail(email)

    if (!normalizedEmail || !password) {
      setError(t('auth.errors.requiredEmailPassword'))
      return
    }

    setLoading(true)
    setError('')
    setNotice('')
    setUnconfirmedEmail('')
    setEmail(normalizedEmail)
    let signedIn = false

    try {
      const { idToken, refreshToken } = await signIn(normalizedEmail, password)
      setTokens(idToken, refreshToken)
      signedIn = true
      await runBootstrap(normalizedEmail)
    } catch (err) {
      if (isUserNotConfirmedError(err)) {
        setUnconfirmedEmail(normalizedEmail)
        setError(t('auth.errors.unconfirmed'))
      } else if (signedIn) {
        setBootstrapError(true)
        setError('')
      } else {
        setError(getCognitoErrorMessage(err, t))
      }
      setLoading(false)
    }
  }

  async function handleRetryBootstrap() {
    const normalizedEmail = normalizeEmail(email)
    setLoading(true)
    setError('')
    try {
      await runBootstrap(normalizedEmail)
    } catch {
      setBootstrapError(true)
      setLoading(false)
    }
  }

  async function handleResendCode() {
    const normalizedEmail = normalizeEmail(unconfirmedEmail || email)
    if (!normalizedEmail) {
      setError(t('auth.errors.requiredEmail'))
      return
    }

    setResending(true)
    setError('')
    setNotice('')
    try {
      await resendConfirmationCode(normalizedEmail)
      setUnconfirmedEmail(normalizedEmail)
      setNotice('auth.verification.newCodeSent')
    } catch (err) {
      setError(getCognitoErrorMessage(err, t))
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-4 flex justify-end">
          <LanguageSwitch />
        </div>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div
            className="inline-flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-12 h-12 rounded-2xl bg-brand-600 flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-900 text-xl leading-tight">WorkflowPro</p>
              <p className="text-gray-500 text-sm leading-tight">{t('auth.brandTagline')}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-8"
        >
          {bootstrapError ? (
            <WorkspaceBootstrapErrorView
              loading={loading}
              onRetry={handleRetryBootstrap}
              onLogout={logoutAfterBootstrapFailure}
            />
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{t('auth.signIn.welcome')}</h1>
                <p className="text-gray-500 mt-1">{t('auth.signIn.subtitle')}</p>
              </div>

              {notice && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5"
            >
              <span className="text-green-700 text-sm font-medium">{t(notice)}</span>
            </motion.div>
              )}

              {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5"
            >
              <span className="text-red-500 text-sm font-medium">{error}</span>
              {unconfirmedEmail && (
                <div className="mt-3 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(buildConfirmEmailPath(unconfirmedEmail), { state: { email: unconfirmedEmail } })}
                    className="text-sm text-brand-600 hover:text-brand-700 font-semibold"
                  >
                    {t('auth.signIn.verifyAccount')}
                  </button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resending}
                    className="text-sm text-brand-600 hover:text-brand-700 font-semibold disabled:opacity-50"
                  >
                    {resending ? t('auth.common.sending') : t('auth.signIn.resendCode')}
                  </button>
                </div>
              )}
            </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label className="label">{t('auth.common.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  className="input pl-10"
                  placeholder={t('auth.common.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <div className="flex items-center justify-between mb-1.5">
                <label className="label mb-0">{t('auth.common.password')}</label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  {t('auth.signIn.forgotPassword')}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input pl-10 pr-10"
                  placeholder={t('auth.signIn.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={t(showPassword ? 'auth.common.hidePassword' : 'auth.common.showPassword')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              size="lg"
              loading={loading}
              icon={<ArrowRight className="w-4 h-4" />}
              iconPosition="right"
            >
              {t('auth.common.signIn')}
            </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              {t('auth.signIn.noAccount')}{' '}
              <button
                onClick={() => navigate('/signup')}
                className="text-brand-600 hover:text-brand-700 font-semibold"
              >
                {t('auth.signIn.createFree')}
              </button>
            </p>
              </div>
            </>
          )}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center text-xs text-gray-400 mt-6"
        >
          {t('auth.signIn.legalPrefix')}{' '}
          <Link to="/terms" className="text-brand-600 hover:text-brand-700 font-medium">
            {t('auth.signIn.terms')}
          </Link>
          {' '}{t('auth.signIn.and')}{' '}
          <Link to="/privacy" className="text-brand-600 hover:text-brand-700 font-medium">
            {t('auth.signIn.privacy')}
          </Link>
        </motion.p>

      </div>
    </div>
  )
}
